import Debug from 'debug';

const debug = Debug('aumico:colgroup-widths');

const parseWidthToNumber = (value: string | null | undefined): number | null => {
    if (!value) {
        return null;
    }

    const trimmed = value.trim().toLowerCase();
    if (trimmed.endsWith('%')) {
        return null;
    }
    if (trimmed.endsWith('px')) {
        const parsed = Number.parseFloat(trimmed.replace('px', ''));
        return Number.isFinite(parsed) ? Math.round(parsed) : null;
    }

    const parsed = Number.parseFloat(trimmed);
    return Number.isFinite(parsed) ? Math.round(parsed) : null;
};

export const applyColgroupWidths = (html: string): string => {
    if (!html || !html.includes('<colgroup')) {
        debug('skip: no colgroup', { hasHtml: !!html });
        return html;
    }

    if (typeof DOMParser === 'undefined') {
        debug('skip: DOMParser unavailable');
        return html;
    }

    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    const tables = Array.from(doc.querySelectorAll('table'));
    debug('start: tables', { count: tables.length });

    tables.forEach((table, tableIndex) => {
        const cols = Array.from(table.querySelectorAll('colgroup col')) as HTMLTableColElement[];
        if (!cols.length) {
            debug('table: no cols', { tableIndex });
            return;
        }

        const widths = cols.map((col) => col.style.width || col.getAttribute('width'));
        debug('table: col widths', { tableIndex, widths });

        if (!widths.some((width) => width != null && width !== '')) {
            debug('table: widths empty', { tableIndex });
            return;
        }

        const rows = Array.from(table.querySelectorAll('tr')) as HTMLTableRowElement[];
        rows.forEach((row) => {
            const cells = Array.from(row.children).filter(
                (child) => child.tagName === 'TD' || child.tagName === 'TH',
            ) as HTMLElement[];

            let colIndex = 0;
            cells.forEach((cell) => {
                const colspan = Number.parseInt(cell.getAttribute('colspan') || '1', 10) || 1;
                const rawWidth = widths[colIndex];
                if (rawWidth != null && rawWidth !== '' && !cell.style.width && !cell.getAttribute('width')) {
                    const normalized = rawWidth.trim();
                    debug('cell: apply width', {
                        tableIndex,
                        colIndex,
                        colspan,
                        rawWidth,
                    });
                    if (normalized.endsWith('%')) {
                        // Set width attribute and style attribute for percentage widths
                        cell.setAttribute('width', normalized);
                        // Get existing style and append width
                        const existingStyle = cell.getAttribute('style') || '';
                        const newStyle = existingStyle + (existingStyle && !existingStyle.endsWith(';') ? '; ' : '') + `width: ${normalized};`;
                        cell.setAttribute('style', newStyle);
                    } else if (normalized.endsWith('px')) {
                        const px = parseWidthToNumber(normalized);
                        if (px != null) {
                            cell.setAttribute('width', `${px}`);
                            const existingStyle = cell.getAttribute('style') || '';
                            const newStyle = existingStyle + (existingStyle && !existingStyle.endsWith(';') ? '; ' : '') + `width: ${px}px;`;
                            cell.setAttribute('style', newStyle);
                        } else {
                            const existingStyle = cell.getAttribute('style') || '';
                            const newStyle = existingStyle + (existingStyle && !existingStyle.endsWith(';') ? '; ' : '') + `width: ${normalized};`;
                            cell.setAttribute('style', newStyle);
                        }
                    } else {
                        const px = parseWidthToNumber(normalized);
                        if (px != null) {
                            cell.setAttribute('width', `${px}`);
                            const existingStyle = cell.getAttribute('style') || '';
                            const newStyle = existingStyle + (existingStyle && !existingStyle.endsWith(';') ? '; ' : '') + `width: ${px}px;`;
                            cell.setAttribute('style', newStyle);
                        } else {
                            const existingStyle = cell.getAttribute('style') || '';
                            const newStyle = existingStyle + (existingStyle && !existingStyle.endsWith(';') ? '; ' : '') + `width: ${normalized};`;
                            cell.setAttribute('style', newStyle);
                        }
                    }
                } else if (rawWidth != null && rawWidth !== '') {
                    debug('cell: skip width', {
                        tableIndex,
                        colIndex,
                        colspan,
                        rawWidth,
                        hasStyle: !!cell.style.width,
                        hasAttr: !!cell.getAttribute('width'),
                    });
                }
                colIndex += colspan;
            });
        });

        if (!table.getAttribute('width') && !table.style.width) {
            table.style.width = '100%';
            debug('table: set width 100%', { tableIndex });
        }
    });

    return doc.body.innerHTML;
};

export const parseColWidthToNumber = (value: string | null | undefined): number | null => {
    return parseWidthToNumber(value);
};
